import { Input, Label } from '@skylabs-digital/backoffice-kit';
import { type KnownTenant, tenantsApi } from '../../api/tenants.api';
import { useSelectedApp } from '../../context/SelectedApp';

/**
 * Selector de app y de tenant compartido por las pestañas de Preferences.
 *
 * Antes cada pestaña pedía los ids a mano y no mandaba `appId`, así que las
 * escrituras daban 403 y las lecturas volvían vacías: no era solo incomodidad
 * de copiar y pegar un UUID, la pantalla no podía operar nada.
 *
 * El picker de tenant se llena con los tenants que noten conoce (`GET /tenants`,
 * derivado de notificaciones y preferencias). Como esa lista no es el padrón de
 * idachu, siempre queda la opción de escribir un id que todavía no aparezca.
 */

const OTHER = '__other__';

/**
 * Normaliza lo que devuelva la API: el array pelado, o uno anidado en `data`.
 * El tipo dice `KnownTenant[]`, pero el tipo no corre en produccion.
 */
export function toTenantList(value: unknown): KnownTenant[] {
  if (Array.isArray(value)) return value as KnownTenant[];
  const nested = (value as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(nested) ? (nested as KnownTenant[]) : [];
}

export interface Scope {
  /** Viene del selector global del header, no de esta pantalla. */
  appId: string;
  tenantId: string;
}

interface ScopePickerProps {
  scope: Scope;
  onChange: (scope: Scope) => void;
  /** Algunas pestañas (Opt-Outs) no usan tenant. */
  showTenant?: boolean;
  /** Texto que explica para qué se usa el tenant en esta pestaña. */
  tenantHint?: string;
}

export function ScopePicker({ scope, onChange, showTenant = true, tenantHint }: ScopePickerProps) {
  // Una app con `tenancy: 'none'` en idachu (resuelto) no tiene tenants: pedir
  // uno seria pedir algo que no existe, y ofrecerlo sugiere que falta elegir
  // algo cuando no falta nada.
  const { hasNoTenants } = useSelectedApp();
  const { data: tenants, loading: tenantsLoading } = tenantsApi
    .withQuery(scope.appId ? { appId: scope.appId } : {})
    .useList();

  // Defensivo a proposito: un envelope inesperado NO puede tumbar la pantalla.
  // Paso en QA el 2026-09-03 — el endpoint devolvia el array anidado en otro
  // `{ data }`, `useList()` entrego un objeto y el `.some()` de abajo dejo
  // Preferences en blanco. El picker es un accesorio; si su lista viene rara,
  // lo peor que puede pasar es quedarse sin opciones sugeridas.
  const known: KnownTenant[] = toTenantList(tenants);
  const isKnown = known.some((t) => t.tenantId === scope.tenantId);
  const showFreeText = scope.tenantId !== '' && !isKnown;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* La app se elige UNA vez, en el header. Antes cada pantalla tenia su
        propio selector y habia que re-elegirla al navegar. */}
      <div className="space-y-2">
        <Label>App</Label>
        <p className="text-sm text-foreground">
          {scope.appId ? 'Usando la app seleccionada en el header.' : 'Elegí una app en el header.'}
        </p>
        <p className="text-xs text-muted-foreground">
          {hasNoTenants
            ? 'Esta app no maneja tenants: sus preferencias valen para toda la app.'
            : 'Todo lo de esta pantalla es por app. Sin elegirla, la API rechaza las escrituras.'}
        </p>
      </div>

      {showTenant && !hasNoTenants ? (
        <div className="space-y-2">
          <Label htmlFor="scope-tenant">Tenant</Label>
          <select
            id="scope-tenant"
            value={showFreeText ? OTHER : scope.tenantId}
            onChange={(e) =>
              onChange({ ...scope, tenantId: e.target.value === OTHER ? ' ' : e.target.value })
            }
            disabled={!scope.appId || tenantsLoading}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{tenantsLoading ? 'Cargando…' : 'Sin tenant'}</option>
            {known.map((t) => (
              <option key={t.tenantId} value={t.tenantId}>
                {t.tenantId}
                {t.notifications > 0 ? ` · ${t.notifications} envíos` : ' · sin tráfico'}
              </option>
            ))}
            <option value={OTHER}>Otro (escribir id)…</option>
          </select>
          {showFreeText ? (
            <Input
              aria-label="Tenant ID"
              value={scope.tenantId.trim()}
              onChange={(e) => onChange({ ...scope, tenantId: e.target.value })}
              placeholder="Pegá el tenant ID"
            />
          ) : null}
          <p className="text-xs text-muted-foreground">
            {tenantHint ??
              'La lista sale de lo que noten ya vio; un tenant sin tráfico se escribe a mano.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
